// Task #2515 — pure inbox-search helpers extracted from app-messages.tsx so the
// logic that decides WHICH conversations match a query is unit-testable on its
// own (the highlightSnippet helper only decides what text to bold).
//
// The matching model is a combined-corpus AND: every word in the query must be
// satisfiable by EITHER an in-memory field (member name / property name / last
// message preview) OR the server's per-word message-content match set. This lets
// words split across the name and old message history all contribute to the same
// query — e.g. "alice trash cans" matches a member named "Alice" who once wrote
// about trash cans.

// Minimal row shape the search logic depends on. The real InboxRow has more
// fields; we only need these for matching/ranking.
export type SearchableInboxRow = {
  memberId: number;
  memberName: string;
  propertyName: string | null;
  lastChatPreview: string | null;
};

// Derive the normalized search words from the raw query text: trim outer
// whitespace, lowercase, split on any run of whitespace, and drop empty pieces.
export function deriveSearchWords(raw: string): string[] {
  return raw.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

// True when an in-memory field of the row contains the (already-lowercased) word.
function wordInMemory(row: SearchableInboxRow, word: string): boolean {
  return (
    String(row.memberName ?? "").toLowerCase().includes(word) ||
    String(row.propertyName ?? "").toLowerCase().includes(word) ||
    String(row.lastChatPreview ?? "").toLowerCase().includes(word)
  );
}

// True when the server's per-word content-match set claims this member matched
// the word in message history. `null` means server results haven't arrived yet,
// so every content lookup is treated as a miss.
function wordInContent(
  row: SearchableInboxRow,
  word: string,
  contentMatchIdsByWord: Record<string, Set<number>> | null,
): boolean {
  return (
    contentMatchIdsByWord != null &&
    (contentMatchIdsByWord[word]?.has(row.memberId) ?? false)
  );
}

// A row matches when EVERY query word is satisfied by either corpus.
export function rowMatchesSearch(
  row: SearchableInboxRow,
  words: string[],
  contentMatchIdsByWord: Record<string, Set<number>> | null,
): boolean {
  return words.every(
    (w) => wordInMemory(row, w) || wordInContent(row, w, contentMatchIdsByWord),
  );
}

// Rank a matched row so the most relevant results sort first:
//   2 = all words match the member name (full name match)
//   1 = at least one word matches any in-memory field (partial name / property /
//       preview hit, with the remaining words satisfied by content search)
//   0 = content-only match — no in-memory field matches any query word
export function rankRow(row: SearchableInboxRow, words: string[]): number {
  const nameLower = String(row.memberName ?? "").toLowerCase();
  if (words.every((w) => nameLower.includes(w))) return 2;
  if (words.some((w) => wordInMemory(row, w))) return 1;
  return 0;
}

// Filter + rank inbox rows for a search query. Empty `words` returns the rows
// unchanged (no active search). Matching rows are sorted by rank (desc) with a
// stable tiebreak that preserves the inbox's original ordering within a rank.
export function filterInboxRows<R extends SearchableInboxRow>(
  rows: R[],
  words: string[],
  contentMatchIdsByWord: Record<string, Set<number>> | null,
): R[] {
  if (words.length === 0) return rows;
  return rows
    .filter((r) => rowMatchesSearch(r, words, contentMatchIdsByWord))
    .map((r, idx) => ({ r, rank: rankRow(r, words), idx }))
    .sort((a, b) => b.rank - a.rank || a.idx - b.idx)
    .map(({ r }) => r);
}
